import {
    CirclesThreePlus,
    Code,
    FileJs,
    FileSearch,
    Gauge,
    GearSix,
    IdentificationCard,
    ImageSquare,
    Layout,
    Package,
    PaintBrush,
    ShareNetwork,
    TerminalWindow,
    ToggleRight,
    Translate,
    Users,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import type { Identifier } from 'dnd-core';
import type React from 'react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { TabType } from '../../types/tab';
import ButtonIcon from '../buttons/ButtonIcon';
import ContextMenu from '../generic/ContextMenu';
import MenuItem from '../generic/MenuItem';
import ConnectorIcon from '../icons/ConnectorIcon';
import FlowIcon from '../icons/FlowIcon';
import '../../../ts/components/generic/css/ContextMenu.css';
import type { ContextMenuItem } from '../../types/generic';
import type { ElementSelection } from '../page-editor/components/gui/composer/ComposerProvider';

type DragItem = {
    index: number;
    tabKey: string;
    tabType: TabType;
};

interface Props {
    title: string | null;
    tabKey: string;
    elementId: string | null;
    isActive: boolean;
    tabType: TabType;
    showCloseButton: boolean;
    showTitle: boolean;
    onSelect: (tabType: TabType, tabKey: string) => void;
    onClose: (tabKey: string) => void;
    order: number;
    onReOrderTabs: (tabKey: string, index: number) => void;
    closeAllTabs: () => void;
    closeTabsToRight: (element: ElementSelection) => void;
    closeOtherTabs: (element: ElementSelection) => void;
}

const TabItem = ({
    tabKey,
    elementId,
    isActive,
    tabType,
    title,
    showCloseButton,
    showTitle,
    onSelect,
    onClose,
    order,
    onReOrderTabs,
    closeAllTabs,
    closeTabsToRight,
    closeOtherTabs,
}: Props) => {
    const tabRef = useRef<HTMLDivElement>(null);

    const [{ handlerId, isOverCurrent }, drop] = useDrop<
        DragItem,
        void,
        { handlerId: Identifier | null; isOverCurrent: boolean }
    >({
        accept: 'elementAccept',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
                isOverCurrent: monitor.isOver({ shallow: true }),
            };
        },
        drop(item: DragItem) {
            if (!tabRef.current) {
                return;
            }

            const dragIndex = item.index;
            const dropIndex = order;

            // Don't replace items with themselves
            if (dragIndex === dropIndex) {
                return;
            }

            onReOrderTabs(item.tabKey, dropIndex);

            // Set the tab that has been moved to be the active tab
            onSelect(item.tabType, item.tabKey);
        },
    });

    const [collected, drag, preview] = useDrag({
        type: 'elementAccept',
        item: () => {
            return { index: order, tabKey, tabType };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const handleOnClose = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        onClose(tabKey);
    };

    const renderIcon = () => {
        const props = {
            width: 20,
            height: 20,
        };

        switch (tabType) {
            case 'flows':
            case 'flow':
                return <FlowIcon {...props} />;
            case 'pages':
            case 'page':
            case 'newPage':
                return <Layout {...props} />;
            case 'environments':
            case 'environment':
                return <GearSix {...props} />;
            case 'values':
                return <CirclesThreePlus {...props} />;
            case 'services':
                return <ConnectorIcon {...props} />;
            case 'types':
                return <Package {...props} />;
            case 'identityProviders':
                return <Users {...props} />;
            case 'assets':
                return <ImageSquare {...props} />;
            case 'api':
                return <ShareNetwork {...props} />;
            case 'translations':
                return <Translate {...props} />;
            case 'themes':
                return <PaintBrush {...props} />;
            case 'players':
                return <TerminalWindow {...props} />;
            case 'macros':
                return <Code {...props} />;
            case 'dashboard':
                return <Gauge {...props} />;
            case 'auditing':
                return <FileSearch {...props} />;
            case 'tenant':
                return <IdentificationCard {...props} />;
            case 'organization':
                return <IdentificationCard {...props} />;
            case 'features':
                return <ToggleRight {...props} />;
            case 'components':
                return <FileJs {...props} />;
            default:
                return null;
        }
    };

    const renderTitle = () => {
        if (!showTitle && isActive === false) {
            return null;
        }

        return (
            <span data-testid={`tab-title-${tabKey}`} className="tab-header-item-label">
                {title}
            </span>
        );
    };

    const renderCloseButton = () => {
        if ((!showCloseButton && isActive === false) || tabType === 'flows') {
            return null;
        }

        const className = classNames(
            'tab-icon-wrapper',
            'close-tab',
            `close-tab-${tabType.toLowerCase()}`,
        );

        return (
            <ButtonIcon
                glyph="cross"
                className={className}
                iconClass="tab-icon"
                onClick={handleOnClose}
                title={`Close tab ${title}`}
            />
        );
    };

    const handleSelect = () => {
        if (!isActive && contextState.isOpen === false) {
            onSelect(tabType, tabKey);
        }
    };

    const classes = classNames({
        'tab-header-item': true,
        active: isActive,
        'center-content icon-only': !showTitle,
        'dragging-tab': isOverCurrent,
    });

    drag(drop(tabRef));

    useEffect(() => {
        if (tabRef.current) {
            // This is so that the browsers drag preview image
            // is positioned beneath the cursor so it doesn't get in the
            // way when dragging tabs.
            preview(tabRef.current, { offsetY: -20 });
        }
    }, [preview]);

    useEffect(() => {
        if (collected.isDragging && !isActive) {
            // Make the tab active when initially start dragging
            // Only call onSelect if the tab is not already active to prevent infinite loops
            onSelect(tabType, tabKey);
        }
    }, [collected.isDragging, isActive, onSelect, tabKey, tabType]);

    const [contextState, setContextState] = useState({ isOpen: false, top: 0, left: 0 });

    const openContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event?.preventDefault();
        setContextState({ isOpen: true, top: event.pageY, left: event.pageX });
    };

    const closeContextMenu = () => {
        setContextState({ isOpen: false, top: 0, left: 0 });
    };

    const closeTab = () => {
        onClose(tabKey);
    };

    const menuItems: ContextMenuItem[] = [
        {
            caption: 'Close tabs to the right',
            clickAction: closeTabsToRight,
        },
        {
            caption: 'Close other tabs',
            clickAction: closeOtherTabs,
        },
        {
            caption: 'Close all',
            clickAction: closeAllTabs,
        },
    ];

    if (tabType !== 'flows') menuItems.push({ caption: 'Close', clickAction: closeTab });

    const returnIndex = (index: number) => {
        return index;
    };

    const renderMenuItems: () => Iterable<ReactNode> = () =>
        menuItems.map((menuItem, index) => (
            <div key={returnIndex(index)}>
                <MenuItem
                    closeContextMenu={closeContextMenu}
                    menuItem={menuItem}
                    contextMenuId={tabKey}
                    tabType={tabType}
                />
            </div>
        ));

    return (
        // biome-ignore lint/a11y/useSemanticElements: Requires dedicated refactor to use correct element
        <div
            data-handler-id={handlerId}
            className={classes}
            data-tab-type={tabType}
            data-tab-key={tabKey}
            data-elementid={elementId ?? ''}
            data-testid={`tab-${tabKey}`}
            onClick={handleSelect}
            onContextMenu={(event) => openContextMenu(event)}
            onKeyDown={({ key }) => {
                if (key !== 'Enter' && key !== ' ') {
                    return;
                }
                handleSelect();
            }}
            tabIndex={0}
            title={title ?? ''}
            role="button"
            ref={tabRef}
        >
            <div className="glyphicon">{renderIcon()}</div>
            {renderTitle()}
            {renderCloseButton()}
            {contextState.isOpen && (
                <ContextMenu
                    left={contextState.left}
                    top={contextState.top}
                    renderMenuItems={renderMenuItems}
                    closeContextMenu={closeContextMenu}
                />
            )}
        </div>
    );
};

export default TabItem;
